import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { describeError } from '../../../common/rateLimit';
import { formatDay, OpenFollowUp, personName } from '../contactDetail/contactDetail';
import { CRM_COMPLETE_FOLLOW_UP } from './calls.queries';
import { describeLogged, formatFollowUp, outcomeLabel } from './logCall';
import { forgetCallData, LogCallForm, LoggedCall } from './LogCallForm';

interface LogCallDialogProps {
  contactId: number;
  name: string;
  blocked: null | 'doNotCall' | 'doNotContact';
  /** The number called, for "Wrong number" to mark bad (card 21.5). */
  number?: { id: number | null; text: string } | null;
  onClose: () => void;
  onLogged: (call: LoggedCall) => void;
}

/**
 * The log-a-call form in a dialog, from a contact's page (card 21). Closing it
 * keeps what was typed: it comes back when the dialog opens again.
 */
export const LogCallDialog = ({ contactId, name, blocked, number, onClose, onLogged }: LogCallDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const toast = useToast();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={phone} aria-labelledby="crm-log-call-title">
      <DialogTitle id="crm-log-call-title" sx={{ pr: 6 }}>
        Log a call{name ? ` with ${name}` : ''}
        <IconButton aria-label="Close the form" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <LogCallForm
          contactId={contactId}
          name={name}
          blocked={blocked}
          number={number}
          submitLabel="Log call"
          autoFocus
          onLogged={(call) => {
            toast.success(describeLogged(call, dayjs()));
            onLogged(call);
          }}
          secondary={<Button onClick={onClose}>Close</Button>}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * The person's open follow-up, over their page: when to call, who set it and
 * why. Logging the call back closes it; "Close without calling" is for when
 * they were reached another way.
 */
export const FollowUpNotice = ({
  followUp,
  canLog,
  onLog,
  onClosed,
}: {
  followUp: OpenFollowUp;
  canLog: boolean;
  onLog: () => void;
  onClosed: () => void;
}) => {
  const toast = useToast();
  const [closing, setClosing] = useState(false);
  const [complete, { loading }] = useMutation(CRM_COMPLETE_FOLLOW_UP, {
    update: (cache) => forgetCallData(cache),
  });
  const now = dayjs();
  const who = personName(followUp.admin);

  const close = async () => {
    try {
      await complete({ variables: { callId: followUp.id } });
      toast.success('Follow-up closed.');
      onClosed();
    } catch (error) {
      toast.error(describeError(error, 'The follow-up could not be closed'));
    } finally {
      setClosing(false);
    }
  };

  return (
    <Alert
      severity={followUp.followUpDue ? 'warning' : 'info'}
      icon={<EventRepeatOutlinedIcon />}
      role="status"
      aria-label={followUp.followUpDue ? 'Follow-up due' : 'Follow-up'}
      sx={{ mb: 2, '& .MuiAlert-message': { minWidth: 0, flex: 1 } }}
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canLog && (
            <Button color="inherit" size="small" variant="outlined" onClick={onLog}>
              Log the call
            </Button>
          )}
          {closing ? (
            <>
              <Button color="inherit" size="small" onClick={() => setClosing(false)} disabled={loading}>
                Keep it
              </Button>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={close}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Close it
              </Button>
            </>
          ) : (
            <Button color="inherit" size="small" onClick={() => setClosing(true)}>
              Close without calling…
            </Button>
          )}
        </Box>
      }
    >
      <AlertTitle sx={{ mb: 0.25 }}>
        {followUp.followUpDue ? 'Follow-up due' : 'Follow-up'} {formatFollowUp(dayjs(followUp.followUpAt), now)}
      </AlertTitle>
      <Typography variant="body2">
        Set{who ? ` by ${who}` : ''} on a call on {formatDay(followUp.occurredAt)} ({outcomeLabel(followUp.outcome).toLowerCase()})
        {followUp.notes ? ':' : '.'}
      </Typography>
      {followUp.notes && (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: 'italic' }}>
          "{followUp.notes}"
        </Typography>
      )}
      {closing && (
        <Typography variant="caption" component="p" sx={{ mt: 0.75 }}>
          Close it when they were reached another way. Logging a call closes it by itself.
        </Typography>
      )}
    </Alert>
  );
};
